/**
 * The chat panel's header: who you're talking to, which chat you're in, and
 * the three utilities (new chat, dock position, dismiss).
 *
 * The title is the history control. Rather than adding a fourth icon to a
 * 32px-target row, the thing that names the current chat is the thing that
 * lets you switch it — so "New Teri chat" / the chat's own title doubles as
 * the trigger, and the past chats drop out of it grouped by when you had them.
 */
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { ChevronDownIcon, PlusIcon } from '@heroicons/react/24/outline';
import { DURATION_FAST, EASE_OUT } from '@/lib/motion';
import { useAssistant, type AssistantDock } from '@/contexts/AssistantContext';
import {
  DockFloatingIcon,
  DockSidebarIcon,
  MinimizeDownIcon,
  CollapseRightIcon,
} from '@/components/icons/AssistantModeIcons';
import ConversationHistory from './ConversationHistory';

const TERI_AVATAR = '/tery/teri_ai_avatar_v2.png';

/** An unsent chat has no server title yet — say so plainly. */
export const NEW_CHAT_TITLE = 'New Teri chat';

/** One quiet treatment for every header utility, so no single one shouts. */
export const HEADER_BUTTON =
  'w-8 h-8 rounded-full flex items-center justify-center text-text-tertiary ' +
  'hover:text-text-primary hover:bg-text-tertiary/10 active:scale-[0.92] ' +
  'transition-[color,background-color,transform] duration-fast focus-ring';

const DOCK_OPTIONS: {
  value: AssistantDock;
  label: string;
  hint: string;
  Icon: React.FC<{ className?: string }>;
}[] = [
  {
    value: 'floating',
    label: 'Floating window',
    hint: 'Sits in the corner, over the page',
    Icon: DockFloatingIcon,
  },
  {
    value: 'sidebar',
    label: 'Side panel',
    hint: 'Docks to the right, page makes room',
    Icon: DockSidebarIcon,
  },
];

/**
 * Dock picker. The trigger IS the current position — the same glyph the menu
 * marks as chosen — so the header shows state and offers the change in one
 * 32px target. Radio semantics (`menuitemradio`) because this picks one of
 * two modes rather than firing two different commands.
 */
const DockMenu: React.FC<{
  dock: AssistantDock;
  onSelect: (dock: AssistantDock) => void;
}> = ({ dock, onSelect }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const reduceMotion = useReducedMotion();

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      // Swallow it: Escape closes the menu, not the chat behind it.
      if (e.key === 'Escape') {
        e.stopPropagation();
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const TriggerIcon = dock === 'sidebar' ? DockSidebarIcon : DockFloatingIcon;

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label="Chat position"
        aria-haspopup="menu"
        aria-expanded={open}
        title="Chat position"
        className={HEADER_BUTTON}
        data-testid="teri-dock-trigger"
      >
        <TriggerIcon className="w-[18px] h-[18px]" />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            role="menu"
            aria-label="Chat position"
            initial={
              reduceMotion
                ? { opacity: 0 }
                : { opacity: 0, transform: 'translateY(-4px) scale(0.96)' }
            }
            animate={{ opacity: 1, transform: 'translateY(0px) scale(1)' }}
            exit={
              reduceMotion
                ? { opacity: 0 }
                : { opacity: 0, transform: 'translateY(-4px) scale(0.96)' }
            }
            transition={{ duration: DURATION_FAST, ease: EASE_OUT }}
            // Origin-aware: the menu grows out of its trigger, not from centre.
            style={{ transformOrigin: 'top right' }}
            className="absolute right-0 top-full mt-2 w-[228px] p-1 rounded-xl bg-surface-elevated border border-border-primary/20 shadow-lg z-10"
            data-testid="teri-dock-menu"
          >
            {DOCK_OPTIONS.map(({ value, label, hint, Icon }) => {
              const active = value === dock;
              return (
                <button
                  key={value}
                  type="button"
                  role="menuitemradio"
                  aria-checked={active}
                  onClick={() => {
                    setOpen(false);
                    onSelect(value);
                  }}
                  className={`w-full flex items-start gap-2.5 px-2.5 py-2 rounded-lg text-left transition-colors duration-fast ${
                    active
                      ? 'bg-primary-500/10 text-primary-600 dark:text-primary-400'
                      : 'text-text-primary hover:bg-text-tertiary/10'
                  }`}
                >
                  <Icon
                    className={`w-[18px] h-[18px] shrink-0 mt-px ${
                      active ? '' : 'text-text-tertiary'
                    }`}
                  />
                  <span className="min-w-0">
                    <span className="block text-[13px] font-medium leading-tight">
                      {label}
                    </span>
                    <span className="block text-[11px] text-text-tertiary leading-tight mt-0.5">
                      {hint}
                    </span>
                  </span>
                </button>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const FOCUSABLE =
  'button:not([disabled]), input:not([disabled]), [href], [tabindex]:not([tabindex="-1"])';

interface AssistantHeaderProps {
  /** True in the side-rail dock — only changes the dismiss wording/glyph. */
  railed: boolean;
  onDismiss: () => void;
}

const AssistantHeader: React.FC<AssistantHeaderProps> = ({ railed, onDismiss }) => {
  const {
    dock,
    setDock,
    conversationId,
    conversationTitle,
    newChat,
    openConversation,
    historyOpen,
    setHistoryOpen,
    answeringIds,
  } = useAssistant();
  const reduceMotion = useReducedMotion();
  const headerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [panelStyle, setPanelStyle] = useState<React.CSSProperties>({});

  const title = conversationTitle?.trim() || NEW_CHAT_TITLE;

  // Portaled and fixed rather than absolute inside the panel: the panel is
  // `overflow-hidden` (and transformed while animating), which would clip a
  // long list and break `position: fixed` for anything nested in it.
  const updatePosition = useCallback(() => {
    const rect = headerRef.current?.getBoundingClientRect();
    if (!rect) return;
    setPanelStyle({
      position: 'fixed',
      top: rect.bottom + 4,
      left: rect.left + 8,
      width: Math.max(rect.width - 16, 240),
      maxHeight: `min(420px, ${Math.max(window.innerHeight - rect.bottom - 24, 200)}px)`,
      zIndex: 60,
    });
  }, []);

  useLayoutEffect(() => {
    if (historyOpen) updatePosition();
  }, [historyOpen, updatePosition]);

  useEffect(() => {
    if (!historyOpen) return undefined;
    const onResize = () => updatePosition();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [historyOpen, updatePosition]);

  // Move focus into the list, and hand it back to the trigger on the way out —
  // Tab stays inside while the list is open so the chat behind it is not
  // reachable by keyboard until the member has chosen or dismissed.
  useEffect(() => {
    if (!historyOpen) return undefined;
    const id = window.setTimeout(() => panelRef.current?.focus(), 20);
    // A delete confirmation is portaled outside this panel. While one is up it
    // owns the interaction: dismissing the list here would unmount the dialog
    // between mousedown and click, so the confirm button would never fire.
    const confirming = () => !!document.querySelector('[data-confirm-dialog]');
    const onKeyDown = (e: KeyboardEvent) => {
      if (confirming()) return;
      if (e.key === 'Escape') {
        e.stopPropagation();
        setHistoryOpen(false);
        triggerRef.current?.focus();
        return;
      }
      if (e.key !== 'Tab' || !panelRef.current) return;
      const focusables = Array.from(
        panelRef.current.querySelectorAll<HTMLElement>(FOCUSABLE)
      ).filter((el) => el.offsetParent !== null);
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement;
      if (!e.shiftKey && (active === last || active === panelRef.current)) {
        e.preventDefault();
        first.focus();
      } else if (e.shiftKey && (active === first || active === panelRef.current)) {
        e.preventDefault();
        last.focus();
      }
    };
    const onPointerDown = (e: MouseEvent) => {
      if (confirming()) return;
      const target = e.target as Node;
      if (panelRef.current?.contains(target) || triggerRef.current?.contains(target)) return;
      setHistoryOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('mousedown', onPointerDown);
    return () => {
      window.clearTimeout(id);
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('mousedown', onPointerDown);
    };
  }, [historyOpen, setHistoryOpen]);

  return (
    <div
      ref={headerRef}
      className="shrink-0 h-14 pl-4 pr-2.5 flex items-center gap-2.5 border-b border-black/[0.05] dark:border-white/[0.06]"
    >
      <span className="shrink-0 w-9 h-9 rounded-full overflow-hidden bg-white dark:bg-grey-800 ring-1 ring-black/[0.06] dark:ring-white/10">
        <img src={TERI_AVATAR} alt="" className="w-full h-full object-cover" />
      </span>

      <button
        ref={triggerRef}
        type="button"
        onClick={() => setHistoryOpen(!historyOpen)}
        aria-haspopup="true"
        aria-expanded={historyOpen}
        aria-label={`${title} — show past chats`}
        title="Past chats"
        data-testid="teri-history-trigger"
        className="flex-1 min-w-0 flex items-center gap-1 -ml-1 pl-1 pr-1.5 py-1 rounded-lg text-left hover:bg-text-tertiary/10 transition-colors duration-fast focus-ring"
      >
        <span className="flex-1 min-w-0">
          <span className="block text-sm font-bold text-text-primary leading-tight truncate">
            {title}
          </span>
          <span className="block text-[11px] text-text-tertiary leading-tight truncate">
            {conversationId
              ? 'Switch chats or start a new one'
              : 'Your AI assistant — follows you around'}
          </span>
        </span>
        <ChevronDownIcon
          className={`w-4 h-4 shrink-0 text-text-tertiary transition-transform duration-fast ${
            historyOpen ? 'rotate-180' : ''
          }`}
        />
      </button>

      <div className="shrink-0 flex items-center gap-0.5">
        <button
          type="button"
          onClick={newChat}
          aria-label="New chat"
          title="New chat"
          className={HEADER_BUTTON}
        >
          <PlusIcon className="w-[18px] h-[18px]" strokeWidth={2} />
        </button>

        <DockMenu dock={dock} onSelect={setDock} />

        <button
          type="button"
          onClick={onDismiss}
          aria-label={railed ? 'Close side panel' : 'Minimize chat'}
          title={
            railed
              ? 'Close the panel (your conversation is kept)'
              : 'Minimize (your conversation is kept)'
          }
          className={HEADER_BUTTON}
          data-testid="teri-dismiss"
        >
          {railed ? (
            <CollapseRightIcon className="w-[18px] h-[18px]" />
          ) : (
            <MinimizeDownIcon className="w-[18px] h-[18px]" />
          )}
        </button>
      </div>

      {createPortal(
        <AnimatePresence>
          {historyOpen && (
            <motion.div
              ref={panelRef}
              tabIndex={-1}
              role="dialog"
              aria-label="Past chats"
              data-testid="teri-history"
              style={{ ...panelStyle, transformOrigin: 'top left' }}
              initial={reduceMotion ? { opacity: 0 } : { opacity: 0, y: -4, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={reduceMotion ? { opacity: 0 } : { opacity: 0, y: -4, scale: 0.98 }}
              transition={{ duration: DURATION_FAST + 0.03, ease: EASE_OUT }}
              className="overflow-y-auto scrollbar-thin rounded-xl bg-surface-elevated border border-border-primary/20 shadow-2xl outline-none"
            >
              <ConversationHistory
                activeId={conversationId}
                enabled={historyOpen}
                answeringIds={answeringIds}
                onSelect={(id) => void openConversation(id)}
              />
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </div>
  );
};

export default AssistantHeader;
